import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { 
  Plus, 
  Minus, 
  Edit, 
  TrendingUp, 
  TrendingDown,
  DollarSign 
} from 'lucide-react';
import { formatCurrency } from '@/lib/utils';

interface VersionChange {
  component_id: string;
  description: string;
  old_quantity?: number;
  new_quantity?: number;
  old_unit_cost?: number;
  new_unit_cost?: number;
  old_total?: number;
  new_total?: number;
  quantity?: number;
  unit_cost?: number;
  total_cost?: number;
}

interface VersionChanges {
  added: VersionChange[];
  removed: VersionChange[];
  modified: VersionChange[];
}

interface VersionSummaryProps {
  baseVersionData: any;
  compareVersionData: any;
  baseVersionNumber: number;
  compareVersionNumber: number;
  versionType: 'estimate' | 'change_order';
}

const VersionSummary: React.FC<VersionSummaryProps> = ({
  baseVersionData,
  compareVersionData,
  baseVersionNumber,
  compareVersionNumber,
  versionType
}) => {
  

  // Helper to get quantity from either snapshot or live format
  const getQuantity = (item: any): number => {
    if (item.quantity !== undefined) return Number(item.quantity) || 0;
    if (item.quantity_in_base_units !== undefined) return Number(item.quantity_in_base_units) || 0;
    return 0;
  };

  // Helper to get unit cost from either snapshot or live format
  const getUnitCost = (item: any): number => {
    if (item.manual_override && item.manual_unit_cost != null) {
      return Number(item.manual_unit_cost) || 0;
    }
    if (item.unit_cost !== undefined) return Number(item.unit_cost) || 0;
    if (item.unit_cost_snapshot !== undefined) return Number(item.unit_cost_snapshot) || 0;
    return 0;
  };

  // Helper to get total cost from either snapshot or live format
  const getTotalCost = (item: any): number => {
    if (item.manual_override && item.manual_total_cost != null) {
      return Number(item.manual_total_cost) || 0;
    }
    if (item.total_cost !== undefined) return Number(item.total_cost) || 0;
    if (item.total_cost_snapshot !== undefined) return Number(item.total_cost_snapshot) || 0;
    return 0;
  };

  // Calculate totals from line items
  const calculateTotal = (items: any[]) => {
    if (!Array.isArray(items)) return 0;
    return items.reduce((sum, item) => sum + getTotalCost(item), 0);
  };

  const baseItems = Array.isArray(baseVersionData) ? baseVersionData : [];
  const compareItems = Array.isArray(compareVersionData) ? compareVersionData : [];
  
  const baseTotal = calculateTotal(baseItems);
  const compareTotal = calculateTotal(compareItems);
  const totalDifference = compareTotal - baseTotal;
  const isIncrease = totalDifference > 0;

  // Simple comparison - count different items
  const addedItems = compareItems.filter(compareItem => 
    !baseItems.some(baseItem => 
      baseItem.component_id === compareItem.component_id || 
      baseItem.id === compareItem.id
    )
  );

  const removedItems = baseItems.filter(baseItem => 
    !compareItems.some(compareItem => 
      compareItem.component_id === baseItem.component_id || 
      compareItem.id === baseItem.id
    )
  );

  const modifiedItems = compareItems.filter(compareItem => {
    const baseItem = baseItems.find(baseItem => 
      baseItem.component_id === compareItem.component_id || 
      baseItem.id === compareItem.id
    );
    
    if (!baseItem) return false;
    
    const baseQty = getQuantity(baseItem);
    const compareQty = getQuantity(compareItem);
    const baseUnitCostVal = getUnitCost(baseItem);
    const compareUnitCostVal = getUnitCost(compareItem);
    
    return Math.abs(baseQty - compareQty) > 0.001 || Math.abs(baseUnitCostVal - compareUnitCostVal) > 0.01;
  });

  return (
    <Card className="w-full">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            {versionType === 'change_order' ? (
              <Edit className="h-5 w-5 text-orange-500" />
            ) : (
              <Edit className="h-5 w-5 text-blue-500" />
            )}
            {versionType === 'change_order' ? 'Change Order' : 'Version'} v{compareVersionNumber} Summary
          </CardTitle>
          <Badge variant={isIncrease ? 'destructive' : 'default'}>
            {isIncrease ? (
              <TrendingUp className="w-3 h-3 mr-1" />
            ) : (
              <TrendingDown className="w-3 h-3 mr-1" />
            )}
            {isIncrease ? '+' : ''}{formatCurrency(totalDifference)}
          </Badge>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {/* Total Summary */}
        <div className="grid grid-cols-3 gap-4 p-4 bg-muted/50 rounded-lg">
          <div className="text-center">
            <p className="text-sm text-muted-foreground">Base Version v{baseVersionNumber}</p>
            <p className="font-semibold">{formatCurrency(baseTotal)}</p>
          </div>
          <div className="text-center">
            <p className="text-sm text-muted-foreground">New Version v{compareVersionNumber}</p>
            <p className="font-semibold">{formatCurrency(compareTotal)}</p>
          </div>
          <div className="text-center">
            <p className="text-sm text-muted-foreground">Difference</p>
            <p className={`font-semibold ${isIncrease ? 'text-red-600' : 'text-green-600'}`}>
              {isIncrease ? '+' : ''}{formatCurrency(totalDifference)}
            </p>
          </div>
        </div>

        {/* Added Items */}
        {addedItems.length > 0 && (
          <div>
            <h4 className="font-medium text-green-700 flex items-center gap-2 mb-3">
              <Plus className="w-4 h-4" />
              Added Items ({addedItems.length})
            </h4>
            <div className="space-y-2">
              {addedItems.map((item, index) => (
                <div key={index} className="flex justify-between items-center p-3 bg-green-50 rounded-lg border border-green-200">
                  <div>
                    <p className="font-medium">{item.description}</p>
                    <p className="text-sm text-muted-foreground">
                      Qty: {getQuantity(item)} @ {formatCurrency(getUnitCost(item))}
                    </p>
                  </div>
                  <p className="font-semibold text-green-700">
                    +{formatCurrency(getTotalCost(item))}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Removed Items */}
        {removedItems.length > 0 && (
          <div>
            <h4 className="font-medium text-red-700 flex items-center gap-2 mb-3">
              <Minus className="w-4 h-4" />
              Removed Items ({removedItems.length})
            </h4>
            <div className="space-y-2">
              {removedItems.map((item, index) => (
                <div key={index} className="flex justify-between items-center p-3 bg-red-50 rounded-lg border border-red-200">
                  <div>
                    <p className="font-medium">{item.description}</p>
                    <p className="text-sm text-muted-foreground">
                      Qty: {getQuantity(item)} @ {formatCurrency(getUnitCost(item))}
                    </p>
                  </div>
                  <p className="font-semibold text-red-700">
                    -{formatCurrency(getTotalCost(item))}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Modified Items */}
        {modifiedItems.length > 0 && (
          <div>
            <h4 className="font-medium text-orange-700 flex items-center gap-2 mb-3">
              <Edit className="w-4 h-4" />
              Modified Items ({modifiedItems.length})
            </h4>
            <div className="space-y-3">
              {modifiedItems.map((item, index) => {
                const baseItem = baseItems.find(baseItem => 
                  baseItem.component_id === item.component_id || baseItem.id === item.id
                );
                return (
                  <div key={index} className="p-3 bg-orange-50 rounded-lg border border-orange-200">
                    <p className="font-medium mb-2">{item.description}</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
                      <div>
                        <p className="text-muted-foreground">Quantity</p>
                        <p>
                          <span className="text-red-600">{baseItem ? getQuantity(baseItem) : 0}</span>
                          {' → '}
                          <span className="text-green-600">{getQuantity(item)}</span>
                        </p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Unit Cost</p>
                        <p>
                          <span className="text-red-600">{formatCurrency(baseItem ? getUnitCost(baseItem) : 0)}</span>
                          {' → '}
                          <span className="text-green-600">{formatCurrency(getUnitCost(item))}</span>
                        </p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Total</p>
                        <p>
                          <span className="text-red-600">{formatCurrency(baseItem ? getTotalCost(baseItem) : 0)}</span>
                          {' → '}
                          <span className="text-green-600">{formatCurrency(getTotalCost(item))}</span>
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* No Changes */}
        {addedItems.length === 0 && removedItems.length === 0 && modifiedItems.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            <DollarSign className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No line item changes detected</p>
            <p className="text-sm">Only estimate details were modified</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default VersionSummary;